<div class="book-info">
  <Cover {book} />
  <div class="wrapper">
    <h1>{book.title}</h1>

    <p class="authors">{@html formatAuthors(book.authors, { withLinks: true })}</p>

    {#if book.goodreadsRating && book.goodreadsRating.value > 0}
      <div class="rating">
        <Rating rating={book.goodreadsRating} withNotice />
      </div>
    {/if}

    <div class="buttons-block">
      <div class="button-group">
        <PurchaseButtons {book} {purchase} {openPurchaseConfirm} />
      </div>

      {#if !purchase && book.previewFile}
        <div class="preview">
          <Button outlined rounded on:click={openPreviewReader}>
            {$_('preview')}
          </Button>
        </div>
      {/if}
    </div>

    <BookAnnotation annotation={book.annotation} />

    <div class="genres">
      {#each book.genres as genre}
        <Genre {genre} />
      {/each}
    </div>
  </div>
</div>

<style lang="stylus">
  .book-info
    margin: 8px 0 0
    text-align: center

    @media $bp.desktop
      margin-top: 64px
      text-align: left
      display: flex

  .wrapper
    @media $bp.desktop
      margin-left: 46px
      margin-left: 3vw

  h1
    font-size: 24px
    margin: 26px 0 8px

    @media $bp.desktop
      margin-top: 0

  .authors
    font-size: 18px
    color: $warm-grey
    margin 0;

  .authors :global(a)
    color: inherit
    text-decoration: none

  .authors :global(a)
    border-bottom: 1px solid rgba($color, 0.2)

    @media $bp.desktop
      &:hover
        border-color: rgba($color, 0.4)

  .rating
    margin-top 20px

  .buttons-block
    display flex
    flex-direction column-reverse
    justify-content flex-start
    margin-top 20px

    @media $bp.desktop
      flex-direction column

  .button-group
    width 100%
    display flex
    flex-direction column
    margin 0

    > :global(div)
      margin-bottom 10px

    :global(.button)
      width 100%
      :global(svg)
        stroke none

    @media $bp.desktop
      display flex
      flex-direction row

      > :global(div)
        min-width 267px
        margin-bottom 0
        margin-right 10px

      > :global(div:last-child)
        margin-right 0

  .preview
    margin-top 15px
    margin-bottom 15px

  .genres
    text-align: left
</style>

<script>
  import { _ } from 'svelte-intl';
  import { stores } from '@sapper/app';
  import { user, popup } from '../../stores';
  import formatAuthors from '../../helpers/formatAuthors';
  import Button from '../Button.svelte';
  import Rating from '../Rating.svelte';
  import Genre from '../Genre.svelte';
  import * as analytics from '../../helpers/analytics';
  import BookAnnotation from './BookAnnotation.svelte';
  import Shop from '../shop/Shop.svelte';
  import Cover from './Cover.svelte';
  import PurchaseButtons from './PurchaseButtons.svelte';

  export let book;

  let purchase = book.productCard && book.productCard.purchase;

  const { session } = stores();

  const TWO_WEEKS = 2 * 7 * 24 * 60 * 60 * 1000;
  function mkPurchaseFunction(price, userMethod, purchaseType) {
    const doPurchase = async () => {
      let retries = 0;
      while ($user.account.balance < price && retries < 10) {
        await user.waitUpdateAccount();
        retries++;
      }

      if ($user.account.balance < price) {
        // Can't do much about this
        popup.hide();
        return;
      }

      await userMethod.call(user, book.id, book.title, price, book);
      popup.hide();

      purchase = { type: purchaseType, expiresAt: Date.now() + TWO_WEEKS };
    };

    return function() {
      if (!$session.user) {
        user.logIn({
          title: $_('login_required'),
          description: $_('login_required_description')
        });

        return;
      }

      if ($user.account.balance < price) {
        const shortage = price - $user.account.balance;

        popup.show(Shop, {
          title: $_('not_enough_coins'),
          description: $_('not_enough_coins_details', { amount: shortage }),
          showBalance: false,
          bundlesTitle: false,
          focusPrice: shortage,
          onSuccess: doPurchase,
        }, { autoSize: true });

        return;
      }

      doPurchase();
    };
  }

  const onRent = mkPurchaseFunction(book.price.rent, user.rentBook, 'RENT');
  const onPurchase = mkPurchaseFunction(book.price.purchase, user.purchaseBook, 'PURCHASE');
  const onRedeem = mkPurchaseFunction(book.price.redeem, user.redeemBook, 'PURCHASE');

  async function openPurchaseConfirm(event) {
    event.preventDefault();

    const { default: PurchaseConfirm } = await import('../PurchaseConfirm.svelte');
    popup.show(PurchaseConfirm, {
      price: book.price,
      purchase,
      onRent,
      onPurchase,
      onRedeem
    }, { autoSize: true });
  }

  async function showPaywall() {
    if (!book.price || !book.price.rent) {
      popup.hide();
      return;
    }

    const { default: Paywall } = await import('../Paywall.svelte');
    popup.show(Paywall, { price: book.price, onRent }, { autoSize: true });
  }

  async function openPreviewReader() {
    if (!book.previewFile) {
      return;
    }

    analytics.readExcerpt(book);

    const previewFileUrl = book.previewFile.url;

    const { default: PreviewReader } = await import('../PreviewReader.svelte');
    popup.show(PreviewReader, { epubUrl: previewFileUrl, onPaywall: showPaywall });
  }
</script>
